import React, { FC, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { Loader } from '@eika/loader';

import { EiendomType, EiendomVariant, NewSoeknadWeb, SoeknadstypeWeb } from '../../generated-types';
import { useGetFellesgjeld } from '../../hooks/useEiendom';
import { useGeoOmraade } from '../../hooks/useGeoOmraade';
import { useBasicSoeknad, useSaveSoeknad } from '../../hooks/useSoeknadMedlaantaker';
import { useTranslation } from '../../translations';
import { laantakerNavn } from '../../util/formatererUtil';
import { ikkeInnenforGeoomraade } from '../../util/geolokasjonUtil';
import { annenEiendom } from '../eiendom/annenEiendom';
import EiendomBruk, { EiendomsBrukFormData } from '../eiendom/eiendomBruk';
import EiendomsVariant, { EiendomsVariantFormData } from '../eiendom/eiendomVariant';
import UserMessage from '../modal/userMessage';
import UserMessageGeoOmraade from '../modal/userMessageGeoOmraade';
import PageCard from '../page/pageCard';
import PageFooter from '../page/pageFooter';
import AndelFellesgjeld, { AndelFellesgjeldFormData } from './andelFellesgjeld';
import BoligAdresse, { BoligAdresseFormData } from './boligAdresse';
import BoligOvertakelse, { BoligOvertakelseFormData } from './boligOvertakelse';
import KjoepeSum, { KjoepeSumFormData } from './kjoepeSum';

export type KjoepeBoligFormData = BoligAdresseFormData &
    EiendomsBrukFormData &
    EiendomsVariantFormData &
    KjoepeSumFormData &
    AndelFellesgjeldFormData &
    BoligOvertakelseFormData;

interface Props {
    soeknadstype: SoeknadstypeWeb;
    navigateTo: string;
}

const KjoepeBolig: FC<Props> = ({ soeknadstype, navigateTo }) => {
    const navigate = useNavigate();
    const { kjoepBolig } = useTranslation();
    const { data: soeknad, isLoading: isLoadingSoeknad, isFetchedAfterMount } = useBasicSoeknad(soeknadstype);
    const { mutate: saveSoeknad, isPending: saveingSoeknad } = useSaveSoeknad(() => {
        navigate(navigateTo);
    });
    const methods = useForm<KjoepeBoligFormData>({
        defaultValues: { adresse: '', postnummer: '', poststed: '', andelFellesgjeld: 0 },
    });

    const { watch, reset, handleSubmit } = methods;
    const { eiendomVariant, postnummer, matrikkel, poststed, fraAdresseSoek, andel } = watch();

    const visEiendomsVariant =
        !fraAdresseSoek ||
        eiendomVariant === EiendomVariant.AKSJELEILIGHET ||
        !(
            poststed &&
            (eiendomVariant !== EiendomVariant.BORETTSANDEL || andel) &&
            (matrikkel || eiendomVariant !== EiendomVariant.SELVEIER)
        );

    const { data: geoOmraade, isFetched: isFetchedGeoOmraade } = useGeoOmraade();
    const { data: andelFellesgjeld } = useGetFellesgjeld(andel?.andelsnummer, andel?.organisasjonsnummer);

    useEffect((): void => {
        if (soeknad?.eiendom) {
            reset({
                adresse: soeknad.eiendom.adresse || undefined,
                postnummer: soeknad.eiendom.postnummer || undefined,
                poststed: soeknad.eiendom.poststed || undefined,
                eiendomBruk: soeknad.eiendom.bruk || undefined,
                eiendomVariant: soeknad.eiendom.variant || undefined,
                overtakelsesDato: soeknad.eiendom.overtakelsesDato || undefined,
                kjoepeSum: soeknad.eiendom.eiendomsverdiWeb?.verdi || undefined,
                andelFellesgjeld: soeknad.eiendom.eiendomsverdiWeb?.fellesgjeld || undefined,
                matrikkel: soeknad.eiendom.matrikkelWeb || undefined,
                andel:
                    soeknad.eiendom.variant === EiendomVariant.SELVEIER
                        ? soeknad.eiendom.borettslagAndelWeb || undefined
                        : undefined,
            });
        }
    }, [soeknad, reset]);

    const onSubmitKjoepBolig = (data: KjoepeBoligFormData) => {
        const eiendom = soeknad?.eiendom ? soeknad.eiendom : annenEiendom;
        eiendom.adresse = data.adresse;
        eiendom.postnummer = data.postnummer;
        eiendom.poststed = data.poststed;
        eiendom.overtakelsesDato = data.overtakelsesDato;
        eiendom.bruk = data.eiendomBruk;
        eiendom.variant = data.eiendomVariant || null;
        eiendom.type = EiendomType.BUYING;
        eiendom.matrikkelWeb = data.eiendomVariant === EiendomVariant.SELVEIER ? data.matrikkel : null;
        eiendom.borettslagAndelWeb =
            data.andel && data.eiendomVariant === EiendomVariant.BORETTSANDEL && data.andel.andelsnummer !== 'Vet ikke'
                ? {
                      andelsnummer: data.andel.andelsnummer,
                      foretaksnavn: data.andel.foretaksnavn,
                      organisasjonsnummer: data.andel.organisasjonsnummer,
                  }
                : null;
        eiendom.eiendomsverdiWeb = {
            id: null,
            verdi: data.kjoepeSum,
            fellesgjeld: andelFellesgjeld && andelFellesgjeld > 0 ? andelFellesgjeld : data.andelFellesgjeld,
            markedsverdiDato: null,
            harKryssikkerhetEllerEksterneHeftelser: null,
        };
        const newSoeknad: NewSoeknadWeb = {
            ...soeknad,
            eiendom,
        } as NewSoeknadWeb;
        saveSoeknad(newSoeknad);
    };

    if (isLoadingSoeknad) return <Loader className="padding-top-extra-large" />;

    const skalViseAndelFellesgjeld = !andelFellesgjeld && (andel || eiendomVariant === EiendomVariant.BORETTSANDEL);

    return (
        <div>
            {isFetchedAfterMount && soeknad && <UserMessage soeknad={soeknad} />}
            {isFetchedGeoOmraade && geoOmraade && ikkeInnenforGeoomraade(geoOmraade, postnummer) && (
                <UserMessageGeoOmraade />
            )}
            <PageCard title={kjoepBolig.title}>
                <FormProvider {...methods}>
                    <form onSubmit={handleSubmit(onSubmitKjoepBolig)}>
                        <div className="row row-gap-large">
                            <span className="typography-ingress__light">
                                <span>{`${kjoepBolig.formTitle} ${laantakerNavn(soeknad?.laantaker)}`}</span>
                                <br />
                                <span>{kjoepBolig.formSubTitle}</span>
                            </span>
                            <BoligAdresse />
                            {visEiendomsVariant && <EiendomsVariant soeknadstype={soeknadstype} />}
                            <KjoepeSum />
                            {skalViseAndelFellesgjeld && <AndelFellesgjeld />}
                            <EiendomBruk />
                            <BoligOvertakelse />
                            <PageFooter soeknadId={soeknad?.soeknadId} showSpinner={saveingSoeknad} startPage />
                        </div>
                    </form>
                </FormProvider>
            </PageCard>
        </div>
    );
};

export default KjoepeBolig;
